import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { toast } from "sonner";
import { User, Mail, Phone, Building2, Lock, Save, Eye, EyeOff, Calendar, Shield, Sun, Moon, MailCheck } from "lucide-react";
import { useTheme } from "../../contexts/ThemeContext";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const getAuthConfig = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
});

const MyAccountTab = ({ user, setUser }) => {
  const { theme, setTheme, isDark } = useTheme();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company_name: "",
  });
  const [passwordForm, setPasswordForm] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [saving, setSaving] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [sendingResetLink, setSendingResetLink] = useState(false);
  const [resetLinkSent, setResetLinkSent] = useState(false);
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "",
        email: user.email || "",
        phone: user.phone || "",
        company_name: user.company_name || "",
      });
    }
  }, [user]);

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    if (!form.email.trim()) {
      toast.error("Email is required");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.put(`${API}/auth/account`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        company_name: form.company_name,
      }, getAuthConfig());
      const updatedUser = { ...user, ...res.data.user };
      localStorage.setItem("user", JSON.stringify(updatedUser));
      setUser(updatedUser);
      toast.success("Account updated successfully");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update account");
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async () => {
    if (!passwordForm.current_password) {
      toast.error("Enter your current password");
      return;
    }
    if (!passwordForm.new_password || passwordForm.new_password.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error("Passwords don't match");
      return;
    }
    setChangingPassword(true);
    try {
      await axios.put(`${API}/auth/account`, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password,
      }, getAuthConfig());
      setPasswordForm({ current_password: "", new_password: "", confirm_password: "" });
      toast.success("Password changed successfully");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change password");
    } finally {
      setChangingPassword(false);
    }
  };

  const handleSendResetLink = async () => {
    if (!user?.email) {
      toast.error("No email on file");
      return;
    }
    setSendingResetLink(true);
    try {
      await axios.post(`${API}/email/password-reset-request`, { email: user.email });
      setResetLinkSent(true);
      toast.success(`Password reset link sent to ${user.email}`);
      // Re-enable the button after 60s so user can retry if email didn't arrive
      setTimeout(() => setResetLinkSent(false), 60000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send reset link");
    } finally {
      setSendingResetLink(false);
    }
  };

  const createdDate = user?.created_at
    ? new Date(user.created_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })
    : "—";

  return (
    <div className="space-y-6" data-testid="my-account-tab">
      {/* Account Info Card */}
      <Card className="glass-card border-theme">
        <CardHeader className="pb-4">
          <CardTitle className="text-foreground text-lg font-semibold flex items-center gap-2">
            <User className="w-5 h-5 text-blue-500" />
            Account Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-muted-foreground text-sm flex items-center gap-1.5">
                <User className="w-3.5 h-3.5" /> Full Name
              </Label>
              <Input
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder="Your name"
                className="bg-theme-surface border-theme text-foreground"
                data-testid="account-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-muted-foreground text-sm flex items-center gap-1.5">
                <Mail className="w-3.5 h-3.5" /> Email Address
              </Label>
              <Input
                type="email"
                value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })}
                placeholder="you@company.com"
                className="bg-theme-surface border-theme text-foreground"
                data-testid="account-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-muted-foreground text-sm flex items-center gap-1.5">
                <Phone className="w-3.5 h-3.5" /> Phone Number
              </Label>
              <Input
                value={form.phone}
                onChange={e => setForm({ ...form, phone: e.target.value })}
                placeholder="(555) 123-4567"
                className="bg-theme-surface border-theme text-foreground"
                data-testid="account-phone-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-muted-foreground text-sm flex items-center gap-1.5">
                <Building2 className="w-3.5 h-3.5" /> Company Name
              </Label>
              <Input
                value={form.company_name}
                onChange={e => setForm({ ...form, company_name: e.target.value })}
                placeholder="Your company"
                className="bg-theme-surface border-theme text-foreground"
                data-testid="account-company-input"
              />
            </div>
          </div>

          {/* Read-only info */}
          <div className="grid sm:grid-cols-3 gap-4 pt-3 border-t border-theme">
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground flex items-center gap-1"><Shield className="w-3 h-3" /> Role</p>
              <p className="text-sm text-foreground font-medium capitalize">{user?.role || "Owner"}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground flex items-center gap-1"><Calendar className="w-3 h-3" /> Member Since</p>
              <p className="text-sm text-foreground font-medium">{createdDate}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground flex items-center gap-1"><User className="w-3 h-3" /> Account ID</p>
              <p className="text-sm text-foreground font-mono text-xs">{user?.user_id || "—"}</p>
            </div>
          </div>

          <div className="flex justify-end pt-2">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#FFCC00] text-black hover:bg-[#FFCC00]/90"
              data-testid="account-save-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Color Mode Card */}
      <Card className="glass-card border-theme">
        <CardHeader className="pb-4">
          <CardTitle className="text-foreground text-lg font-semibold flex items-center gap-2">
            {isDark ? <Moon className="w-5 h-5 text-indigo-400" /> : <Sun className="w-5 h-5 text-amber-500" />}
            Preferred Color Mode
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3" data-testid="color-mode-selector">
            <button
              onClick={() => setTheme("light")}
              className={`flex-1 flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${
                theme === "light"
                  ? "border-amber-400 bg-amber-50 dark:bg-amber-500/10"
                  : "border-transparent bg-theme-surface hover:border-border"
              }`}
              data-testid="color-mode-light"
            >
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${theme === "light" ? "bg-amber-100 dark:bg-amber-500/20" : "bg-muted/50"}`}>
                <Sun className={`w-5 h-5 ${theme === "light" ? "text-amber-600" : "text-muted-foreground"}`} />
              </div>
              <div className="text-left">
                <p className={`text-sm font-semibold ${theme === "light" ? "text-amber-700 dark:text-amber-400" : "text-foreground"}`}>Light</p>
                <p className="text-xs text-muted-foreground">Bright & clean</p>
              </div>
            </button>
            <button
              onClick={() => setTheme("dark")}
              className={`flex-1 flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${
                theme === "dark"
                  ? "border-indigo-400 bg-indigo-50 dark:bg-indigo-500/10"
                  : "border-transparent bg-theme-surface hover:border-border"
              }`}
              data-testid="color-mode-dark"
            >
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${theme === "dark" ? "bg-indigo-100 dark:bg-indigo-500/20" : "bg-muted/50"}`}>
                <Moon className={`w-5 h-5 ${theme === "dark" ? "text-indigo-600 dark:text-indigo-400" : "text-muted-foreground"}`} />
              </div>
              <div className="text-left">
                <p className={`text-sm font-semibold ${theme === "dark" ? "text-indigo-700 dark:text-indigo-400" : "text-foreground"}`}>Dark</p>
                <p className="text-xs text-muted-foreground">Easy on the eyes</p>
              </div>
            </button>
          </div>
          <p className="text-xs text-muted-foreground mt-3">Your preference is saved automatically.</p>
        </CardContent>
      </Card>

      {/* Password Change Card */}
      <Card className="glass-card border-theme">
        <CardHeader className="pb-4">
          <CardTitle className="text-foreground text-lg font-semibold flex items-center gap-2">
            <Lock className="w-5 h-5 text-orange-500" />
            Change Password
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="max-w-md space-y-4">
            <div className="space-y-2">
              <Label className="text-muted-foreground text-sm">Current Password</Label>
              <div className="relative">
                <Input
                  type={showCurrentPw ? "text" : "password"}
                  value={passwordForm.current_password}
                  onChange={e => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                  placeholder="Enter current password"
                  className="bg-theme-surface border-theme text-foreground pr-10"
                  data-testid="account-current-pw-input"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPw(!showCurrentPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showCurrentPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-muted-foreground text-sm">New Password</Label>
              <div className="relative">
                <Input
                  type={showNewPw ? "text" : "password"}
                  value={passwordForm.new_password}
                  onChange={e => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                  placeholder="At least 6 characters"
                  className="bg-theme-surface border-theme text-foreground pr-10"
                  data-testid="account-new-pw-input"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPw(!showNewPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showNewPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-muted-foreground text-sm">Confirm New Password</Label>
              <Input
                type="password"
                value={passwordForm.confirm_password}
                onChange={e => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                placeholder="Confirm new password"
                className="bg-theme-surface border-theme text-foreground"
                data-testid="account-confirm-pw-input"
              />
            </div>
          </div>

          <div className="flex justify-end pt-2">
            <Button
              onClick={handlePasswordChange}
              disabled={changingPassword}
              variant="outline"
              className="border-orange-500/50 text-orange-600 hover:bg-orange-500/10"
              data-testid="account-change-pw-btn"
            >
              <Lock className="w-4 h-4 mr-2" />
              {changingPassword ? "Changing..." : "Change Password"}
            </Button>
          </div>

          {/* Reset Password via Email — separate flow */}
          <div className="mt-6 pt-5 border-t border-border">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="max-w-md">
                <p className="text-sm font-medium text-foreground flex items-center gap-1.5">
                  <MailCheck className="w-4 h-4 text-blue-500" />
                  Forgot your current password?
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  We'll email a secure reset link to <span className="font-medium">{user?.email || "your account"}</span>. The link expires in 1 hour.
                </p>
              </div>
              <Button
                onClick={handleSendResetLink}
                disabled={sendingResetLink || resetLinkSent}
                variant="outline"
                size="sm"
                className="border-blue-500/50 text-blue-600 hover:bg-blue-500/10 shrink-0"
                data-testid="account-reset-pw-btn"
              >
                <MailCheck className="w-4 h-4 mr-2" />
                {sendingResetLink ? "Sending..." : resetLinkSent ? "Link Sent ✓" : "Send Reset Link"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MyAccountTab;
